/**
 * Sentry bootstrap. Initialises the SDK only when REACT_APP_SENTRY_DSN is
 * configured — otherwise `init()` is a no-op so dev/preview builds stay silent
 * and don't emit noisy warnings.
 *
 * The exported `captureException` / `captureMessage` helpers are safe to call
 * regardless of whether Sentry is initialised. When disabled they simply do
 * nothing — this lets the app-wide logger forward errors without knowing
 * whether reporting is on.
 */
import * as Sentry from '@sentry/react';

let initialised = false;

export function initSentry() {
  if (initialised) return;
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return; // Silent no-op when unset

  try {
    Sentry.init({
      dsn,
      environment:
        process.env.REACT_APP_SENTRY_ENVIRONMENT ||
        process.env.NODE_ENV ||
        'development',
      release: process.env.REACT_APP_SENTRY_RELEASE || undefined,
      // Conservative defaults — tune once real traffic starts arriving.
      tracesSampleRate: parseFloat(
        process.env.REACT_APP_SENTRY_TRACES_SAMPLE_RATE || '0.1'
      ),
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: parseFloat(
        process.env.REACT_APP_SENTRY_REPLAY_ERROR_SAMPLE_RATE || '0.1'
      ),
      // Filter out noisy chunk-load errors that come from stale service workers.
      beforeSend(event, hint) {
        const err = hint?.originalException;
        const msg = (err && err.message) || event.message || '';
        if (/loading chunk \d+ failed/i.test(msg)) return null;
        return event;
      },
    });
    initialised = true;
  } catch (err) {
    // Never let telemetry break the app.
    // eslint-disable-next-line no-console
    console.warn('[sentry] init failed', err);
  }
}

/** Report an exception to Sentry. No-op when SDK isn't initialised. */
export function captureException(err, context) {
  if (!initialised) return;
  try {
    if (context) {
      Sentry.withScope((scope) => {
        Object.entries(context).forEach(([k, v]) => scope.setExtra(k, v));
        Sentry.captureException(err);
      });
    } else {
      Sentry.captureException(err);
    }
  } catch (_) {
    /* swallow */
  }
}

/** Report a message-only breadcrumb / event. No-op when SDK isn't initialised. */
export function captureMessage(message, level = 'info') {
  if (!initialised) return;
  try {
    Sentry.captureMessage(message, level);
  } catch (_) {
    /* swallow */
  }
}

export default { initSentry, captureException, captureMessage };
